import { Edit3, FileText, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { Col, Row } from "reactstrap";
import { AssistanceNavBar } from "../../components/Assistances/AssistanceNavBar";
import DigitalSignature from "../../components/Assistances/DigitalSignature";
import ViewSignature from "../../components/Assistances/ViewSignature";
import ButtonComponent from "../../components/ButtonComponent";
import ChangeStatusComponent from "../../components/ChangeStatusComponent";
import { generatePDF } from "../../components/GeneratePDF";
import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/Layout/PageTitle";
import { useMobile } from "../../components/Layout/UseResponsive";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import Toast from "../../helpers/Toast";
import { AssistanceDTO } from "../../models/Assistance/AssistanceDTO";
import { DigitalSignatureDTO } from "../../models/Assistance/DigitalSignatureDTO";
import { AssistanceService } from "../../services/AssistanceService";
import "../../styles/AssistanceDetails.css";
import { ExecutionButtons } from "./ExecutionButtons";

export function AssistanceDetails() {
	const intl = useIntl();
	const isMobile = useMobile(606);
	const { availableModules } = useAvailableModulesContext();
	const { assistanceId } = useParams<{ assistanceId: string }>();
	const [isLoadingAssistance, setIsLoadingAssistance] = useState<boolean>(false);
	const assistanceService = new AssistanceService();
	const [assistanceNotFound, setAssistanceNotFound] = useState<boolean>(false);
	const [assistance, setAssistance] = useState<AssistanceDTO>({} as AssistanceDTO);
	const [reloadExecutions, setReloadExecutions] = useState<boolean>(false);
	const [ChangeStatusIsOpen, setChangeStatusIsOpen] = useState<boolean>(false);
	const [reload, setReload] = useState<boolean>(false);
	const [isSignatureOpen, setIsSignatureOpen] = useState(false);
	const [isViewSignatureOpen, setIsViewSignatureOpen] = useState<boolean>(false);
	// Assinatura
	const [isActivateDigitalSignature, setIsActivateDigitalSignature] = useState<boolean>(false);
	const [isActiveErpSupportModule, setIsActiveErpSupportModule] = useState<boolean>(false);
	// Execuções / Tempos
	const [fetchExecutionsData, setFetchExecutionsData] = useState<boolean>(false);
	// Estado para controlar se está obtendo localização
	const [isGettingLocation, setIsGettingLocation] = useState<boolean>(false);
	const [isActiveLocation, setIsActiveLocation] = useState<boolean>(false);

	const onFetchExecutionsData = () => {
		setFetchExecutionsData(!fetchExecutionsData);
	};

	const getAssistance = async () => {
		setIsLoadingAssistance(true);
		var response = await assistanceService.GetById(parseInt(assistanceId ?? "0"));
		setIsLoadingAssistance(false);

		if (response.success !== true || response.obj == null) {
			setAssistanceNotFound(true);
			Toast.Show("error", response.message);
			return;
		}

		setAssistance(response.obj);
	};

	const reloadExecutionsFunction = () => {
		setReloadExecutions(!reloadExecutions);
	};

	const handleSignatureDialog = () => {
		setIsSignatureOpen(!isSignatureOpen);
	};

	const handleSignatureCancel = () => {
		setIsSignatureOpen(false);
	};

	const handleSignatureSave = async (signatureData: DigitalSignatureDTO) => {
		// Chama a API com a assinatura
		const response = await assistanceService.SubmitDigitalSignature(Number(assistance.id), signatureData);
		if (response === null || !response.success) {
			Toast.Show("error", response.message);
		}
		setReload(!reload);

		Toast.Show("success", intl.formatMessage({ id: "AssistanceEdit.SignSuccess" }));
	}

	const handleSignaturePopUp = () => {
		setIsViewSignatureOpen(!isViewSignatureOpen);
	};

	const handleCloseSignaturePopUp = () => {
		setIsViewSignatureOpen(false);
	};

	const handleClearSignature = async () => {
		const response = await assistanceService.ClearDigitalSignature(Number(assistance.id));
		if (response === null || !response.success) {
			Toast.Show("error", intl.formatMessage({ id: "Screens.AssistanceDetails.ClearSignature" }));
		} else {
			Toast.Show("success", intl.formatMessage({ id: "AssistanceEdit.SignatureCleared" }) || "Assinatura limpa com sucesso");
			setIsViewSignatureOpen(false);
			setReload(!reload);
		}
	};

	const handleGetLocation = async () => {
		setIsGettingLocation(true);

		if (!navigator.geolocation) {
			Toast.Show("error", intl.formatMessage({ id: "Screens.AssistanceDetails.LocationNotSupported" }));
			setIsGettingLocation(false);
			return;
		}

		navigator.geolocation.getCurrentPosition(
			(position) => {
				const { latitude, longitude } = position.coords;

				Toast.Show("success", intl.formatMessage({ id: "Screens.AssistanceDetails.LocationSuccess" }, { latitude, longitude }));
				setIsGettingLocation(false);
			},
			(error) => {
				console.error(error);
				Toast.Show("error", intl.formatMessage({ id: "Screens.AssistanceDetails.LocationError" }));
				setIsGettingLocation(false);
			},
			{
				enableHighAccuracy: true,
				timeout: 10000,
				maximumAge: 0,
			}
		);
	};

	//obter valor setting (módulo assinatura)
	useEffect(() => {
		if (availableModules.includes("IsActiveDigitalSignature")) {
			setIsActivateDigitalSignature(true);
		}
		if (availableModules.includes("IsActiveErpSupportModule")) {
			setIsActiveErpSupportModule(true);
		}
		if (availableModules.includes("IsActiveLocation")) {
			setIsActiveLocation(true);
		}
	}, [availableModules]);

	useEffect(() => {
		getAssistance();
	}, [reload]);

	return (
		<Layout>
			<ChangeStatusComponent
				close={() => setChangeStatusIsOpen(false)}
				AssistanceId={assistance.id}
				EquipmentId={assistance.equipment?.id}
				IsOpen={ChangeStatusIsOpen}
				onAdded={(newStatusName: string) => {
					// Optimistic update: immediately update the status in local state
					setAssistance(prev => prev ? { ...prev, status: newStatusName } : prev);
					// Still trigger reload to fetch complete updated data
					setReload(!reload);
				}}
			/>

			{!isMobile ? (
				<div className="pageContainer">
					<Row className="pageHeader" style={{ display: "flex", alignItems: "center", justifyContent: "start" }}>
						<Col xl={4} lg={4} md={12} sm={12} xs={12} style={{ display: "flex", margin: "0px" }}>
							<PageTitle
								title={
									isActiveErpSupportModule
										? `Assistência ${assistance.supportNumber}`
										: `Assistência ${assistance.erpDocType ?? ""} ${assistance.erpSerie ?? ""} ${assistance.erpNumDoc ?? ""} ${assistance.assistanceType === "Reparação"
											? "- Reparação"
											: assistance.assistanceType === "Fabricação"
												? "- Fabricação"
												: ""
										}`
								}
								allowGoBack={true}
							/>
						</Col>
						<Col
							xl={8}
							lg={8}
							md={12}
							sm={12}
							xs={12}
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "end",
							}}
						>

							{isActivateDigitalSignature && (
								<>
									{assistance.digitalSignature === null ? (
										<>
											{assistance.userCanEdit && (
												<>
													<ButtonComponent
														style={{
															marginRight: "1%",
															minWidth: "70px",
														}}
														text={intl.formatMessage({ id: "AssistanceEdit.Signature" })}
														onClick={() => handleSignatureDialog()}
													/>
													{isSignatureOpen && (
														<DigitalSignature
															onCancel={handleSignatureCancel}
															onSave={(dataUrl: string) => {
																const signatureData: DigitalSignatureDTO = {
																	signature: dataUrl,
																};
																handleSignatureSave(signatureData);
															}}
														/>
													)}
												</>
											)}
										</>
									) : (
										<>
											<ButtonComponent
												style={{
													marginRight: "1%",
													minWidth: "70px",
												}}
												text={intl.formatMessage({ id: "AssistanceEdit.SeeSignature" })}
												onClick={() => handleSignaturePopUp()}
											/>
											{isViewSignatureOpen && (
												<ViewSignature
													signature={assistance.digitalSignature ?? ""}
													onClose={handleCloseSignaturePopUp}
													onClear={assistance.userCanEdit ? handleClearSignature : undefined}
													isSaved={true}
												/>
											)}
										</>
									)}
								</>
							)}

							<ButtonComponent
								style={{
									marginRight: "2%", minWidth: "139px"
								}}
								text={intl.formatMessage({ id: "AssistanceEdit.Download" })}
								onClick={() => generatePDF(assistance.erpNumDoc, assistance.erpDocType, assistance.erpSerie)}
							/>

							{isActiveLocation && (
								<ButtonComponent
									style={{ marginRight: "1%", minWidth: "120px" }}
									text={isGettingLocation ? intl.formatMessage({ id: "Screens.AssistanceDetails.GettingLocation" }) : intl.formatMessage({ id: "Screens.AssistanceDetails.GetLocation" })}
									onClick={handleGetLocation}
									disabled={isGettingLocation}
								/>
							)}

							<span style={{ fontWeight: "bold", fontSize: "14px", marginRight: "2%" }}>
								Técnico: {assistance.technician === "" ? "Não atribuído" : assistance.technician}{" "}
							</span>

							<span style={{ fontWeight: "bold", fontSize: "14px", marginRight: "2%" }}>
								Estado: <span style={{ textTransform: "uppercase" }}>{assistance?.status}</span>
							</span>
							{assistance.userCanChangeStatus && (
								<ButtonComponent
									onClick={() => setChangeStatusIsOpen(true)}
									text={intl.formatMessage({ id: "AssistanceDetails.UpdateStatus" })} />
							)}
						</Col>
					</Row>

					<div className="pageBody">
						{/* Se o pedido para obter a assistencia não retornou nada */}
						{assistanceNotFound === true && (
							<div>
								<span className="error">A assistência selecionada não foi encontrada.</span>
							</div>
						)}
						{/* Navbar da assistência */}
						<AssistanceNavBar
							mode={"workshop"}
							setAssistance={setAssistance}
							assistance={assistance}
							isLoadingAssistance={isLoadingAssistance}
							getAssistance={getAssistance}
							reloadExecutions={reloadExecutions}
							childrenColumnSize={6}
							onFetchExecutionsData={onFetchExecutionsData}
						/>

						{/* Botões para começar e completar execução */}
						{!isActiveErpSupportModule &&
							<ExecutionButtons
								assistance={assistance}
								setAssistance={setAssistance}
								reloadExecutions={reloadExecutionsFunction}
								onFetchExecutionsData={onFetchExecutionsData}
								isActiveLocation={isActiveLocation}
							/>
						}

					</div>
				</div>


			) : (
				<div className="pageContainer">
					<Row className="pageHeader">
						<Col xs={12} style={{ margin: ".5rem 0" }}>
							<PageTitle
								title={
									isActiveErpSupportModule
										? `Assistência ${assistance.supportNumber}`
										: `Assistência ${assistance.erpDocType ?? ""} ${assistance.erpSerie ?? ""} ${assistance.erpNumDoc ?? ""} ${assistance.assistanceType === "Reparação"
											? "- Reparação"
											: assistance.assistanceType === "Fabricação"
												? "- Fabricação"
												: ""
										}`
								}
								allowGoBack={true}
							/>
						</Col>
						<Col xs={12} style={{ marginBottom: ".5rem" }}>
							<div className="technician-status-card">
								<div className="technician-info">
									<span className="technician-label">TÉCNICO</span>
									<span className="technician-name">
										{assistance.technician === "" ? "Não atribuído" : assistance.technician}
									</span>
								</div>
								{assistance.userCanChangeStatus ? (
									<div
										className="status-badge"
										onClick={() => setChangeStatusIsOpen(true)}
									>
										{assistance?.status}
										<RefreshCw size={16} />
									</div>
								) : (
									<div className="status-badge">
										{assistance?.status}
									</div>
								)}
							</div>
						</Col>
					</Row>

					<div className="pageBody">
						{/* Se o pedido para obter a assistencia não retornou nada */}
						{assistanceNotFound === true && (
							<div>
								<span className="error">A assistência selecionada não foi encontrada.</span>
							</div>
						)}
						{/* Navbar da assistência */}
						<AssistanceNavBar
							mode={"workshop"}
							setAssistance={setAssistance}
							assistance={assistance}
							isLoadingAssistance={isLoadingAssistance}
							getAssistance={getAssistance}
							reloadExecutions={reloadExecutions}
							childrenColumnSize={6}
							onFetchExecutionsData={onFetchExecutionsData}
						/>

						{/* Botões para começar e completar execução */}
						{!isActiveErpSupportModule &&
							<ExecutionButtons
								assistance={assistance}
								setAssistance={setAssistance}
								reloadExecutions={reloadExecutionsFunction}
								onFetchExecutionsData={onFetchExecutionsData}
								isActiveLocation={isActiveLocation}
							/>
						}

						<div className="button-actions-container">
							<ButtonComponent
								text={intl.formatMessage({ id: "AssistanceEdit.Download" })}
								onClick={() => generatePDF(assistance.erpNumDoc, assistance.erpDocType, assistance.erpSerie)}
								className="outlined"
								icon={<FileText size={18} />}
							/>

							{isActivateDigitalSignature && (
								<>
									{assistance.digitalSignature === null ? (
										<>
											{assistance.userCanEdit && (
												<>
													<ButtonComponent
														text={intl.formatMessage({ id: "AssistanceEdit.Signature" })}
														onClick={() => handleSignatureDialog()}
														icon={<Edit3 size={18} />}
													/>
													{isSignatureOpen && (
														<DigitalSignature
															onCancel={handleSignatureCancel}
															onSave={(dataUrl: string) => {
																const signatureData: DigitalSignatureDTO = {
																	signature: dataUrl,
																};
																handleSignatureSave(signatureData);
															}}
														/>
													)}
												</>
											)}
										</>
									) : (
										<>
											<ButtonComponent
												text={intl.formatMessage({ id: "AssistanceEdit.SeeSignature" })}
												onClick={() => handleSignaturePopUp()}
												icon={<Edit3 size={18} />}
											/>
											{isViewSignatureOpen && (
												<ViewSignature
													signature={assistance.digitalSignature ?? ""}
													onClose={handleCloseSignaturePopUp}
													onClear={assistance.userCanEdit ? handleClearSignature : undefined}
													isSaved={true}
												/>
											)}
										</>
									)}
								</>
							)}
						</div>
					</div>
				</div>
			)}
		</Layout>
	);
}